import { useState } from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  AlertCircleIcon,
  ArrowDown01Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonVariant } from "shared/components";
import { isLeadBulkActionSettled, type LeadBulkAction } from "api/lead";
import { useLeadBulkActionProgress, type LeadBulkActionRun } from ".";
import { LEAD_BULK_ACTION_LABELS } from "./labels";

type Tone = "pending" | "success" | "warning" | "error";

const TONE_ICON: Record<Tone, IconSvgElement | null> = {
  pending: null,
  success: Tick02Icon,
  warning: AlertCircleIcon,
  error: AlertCircleIcon
};

const TONE_COLOR_CLASS: Record<Tone, string> = {
  pending: "",
  success: "text-success-500",
  warning: "text-warning-500",
  error: "text-danger-500"
};

const pluralLeads = (count: number) => (count === 1 ? "lead" : "leads");

const getTone = ({
  isStatusError,
  hasGivenUpPolling,
  status
}: {
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  status: LeadBulkAction | undefined;
}): Tone => {
  if (isStatusError || status?.status === "failed") return "error";
  if (hasGivenUpPolling) return "warning";
  if (status?.status === "completed" && status.failed_count > 0)
    return "warning";
  if (status?.status === "completed") return "success";
  return "pending";
};

const getTitle = ({
  tone,
  isStatusError,
  hasGivenUpPolling,
  run,
  status
}: {
  tone: Tone;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  run: LeadBulkActionRun;
  status: LeadBulkAction | undefined;
}) => {
  const labels = LEAD_BULK_ACTION_LABELS[run.action];
  if (tone === "error" && isStatusError) return "Couldn't check progress";
  if (tone === "error") return "Bulk action failed";
  if (tone === "warning" && hasGivenUpPolling)
    return "Taking longer than expected";
  if (tone === "warning") return "Finished with some errors";
  if (tone === "success") {
    const total = status?.total_count ?? 0;
    return `${total.toLocaleString()} ${pluralLeads(total)} ${labels.done}`;
  }
  const total = status?.total_count;
  return total == null
    ? `${labels.progress} matching leads…`
    : `${labels.progress} ${total.toLocaleString()} ${pluralLeads(total)}…`;
};

const getDescription = ({
  tone,
  isStatusError,
  hasGivenUpPolling,
  status
}: {
  tone: Tone;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  status: LeadBulkAction | undefined;
}) => {
  if (isStatusError)
    return "The action is still running in the background — reload the page to see the result.";
  if (tone === "error")
    return (
      status?.error ||
      "Something went wrong before the action could finish. Leads already processed were kept."
    );
  if (hasGivenUpPolling)
    return "This is taking longer than expected. It's likely still running in the background — reload the page to check on it.";
  if (!status || status.total_count == null)
    return "Finding every lead that matches your filters…";
  if (tone === "warning")
    return `${status.failed_count.toLocaleString()} ${pluralLeads(status.failed_count)} could not be updated. Everything else went through.`;
  if (tone === "success") return "The table has been refreshed.";
  return `${status.processed_count.toLocaleString()} of ${status.total_count.toLocaleString()} ${pluralLeads(status.total_count)}`;
};

const getProgressPercent = (
  status: LeadBulkAction | undefined,
  isSettled: boolean
) => {
  if (!status?.total_count || isSettled) return null;
  return Math.min(
    100,
    Math.round((status.processed_count / status.total_count) * 100)
  );
};

const PanelIcon = ({
  tone,
  icon
}: {
  tone: Tone;
  icon: IconSvgElement | null;
}) => {
  if (!icon)
    return (
      <div className="size-4 shrink-0 animate-spin rounded-full border-2 border-alpha-200 border-t-primary-500" />
    );

  return (
    <HugeiconsIcon
      className={`shrink-0 ${TONE_COLOR_CLASS[tone]}`}
      icon={icon}
      size={20}
    />
  );
};

/**
 * Bottom-right card for a filter-wide leads bulk action (CRM-1449). No cancel by design:
 * the user has already confirmed twice, and partial runs keep what they processed.
 */
export const LeadBulkActionProgressPanel = () => {
  const { run, status, isStatusError, hasGivenUpPolling, dismiss } =
    useLeadBulkActionProgress();
  const [expanded, setExpanded] = useState(true);

  if (run === null) return null;

  const isSettled = Boolean(status && isLeadBulkActionSettled(status.status));
  const tone = getTone({ isStatusError, hasGivenUpPolling, status });
  const title = getTitle({
    tone,
    isStatusError,
    hasGivenUpPolling,
    run,
    status
  });
  const description = getDescription({
    tone,
    isStatusError,
    hasGivenUpPolling,
    status
  });
  const icon = TONE_ICON[tone];
  const progressPercent = getProgressPercent(status, isSettled);
  const canDismiss = isSettled || isStatusError || hasGivenUpPolling;

  return (
    <div className="fixed right-4 bottom-6 z-[9999] w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-alpha-40 bg-background-1000 shadow-xl">
      <button
        className="flex w-full items-center justify-between gap-2 p-4 text-left"
        onClick={() => setExpanded((prev) => !prev)}
        type="button"
      >
        <div className="flex min-w-0 items-center gap-2">
          <PanelIcon icon={icon} tone={tone} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-content-1000">{title}</p>
            {!expanded ? (
              <p className="truncate text-xs text-content-400">{description}</p>
            ) : null}
          </div>
        </div>
        <HugeiconsIcon
          className={`shrink-0 text-content-400 transition-transform ${expanded ? "" : "rotate-180"}`}
          icon={ArrowDown01Icon}
          size={18}
        />
      </button>

      {expanded ? (
        <div className="space-y-2 px-4 pb-4">
          <p className="text-sm text-content-400">{description}</p>
          {progressPercent !== null ? (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-alpha-80">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-200"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2 pt-1">
            {canDismiss ? (
              <Button onClick={dismiss} variant={ButtonVariant.TEXT}>
                Dismiss
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
